import { DOMParser } from 'xmldom';

import { getItemBodyAsHTML, replaceImageUrlsForImgTags, replacePrintedVariables } from './itemBody';
import interactionsParser from './interactions/InteractionsParser';
import { getMediaModels } from './MediaModels';
import { getPDFPanelModel } from './PDFPanelModel';
import { getRubricBlockModel } from './RubricBlockModel';
import { parseAssessmentDOM } from './AssessmentXML';
import questionAssets from './QuestionAssets';
import constants from '../../Constants';
import utilService from '../../../services/UtilService';
import objectTypes from '../../../constants/ObjectTypes';

export function sanitizeResponseDeclarations(responseDeclarations) {
    const declarations = (responseDeclarations || [])
        .map((responseDeclaration) => {
            // Stringify all non string default values
            const defaultValue = (responseDeclaration.defaultValue || [])
                .map((_defaultValue) => {
                    return typeof _defaultValue !== 'string' ? JSON.stringify(_defaultValue) : _defaultValue;
                });
            return { ...responseDeclaration,
                defaultValue,
            };
        });

    return utilService.uniqueValuesArray(
        item => item.identifier,
        declarations,
    );
}

/**
 * Entry point for parsing a question. It uses the responseDeclarations array and itemBody. ItemBody gets converted to a DOM element
 * and sent into two parsers: componentBody and responseModels.
 * The result from them are merged with the qti json on the server, so that the original data is persisted.
 * @param qtiData {{contentRevisionId, identifier, responseDeclarations, itemBody}}
 * @param language
 * @param assessmentRunId - optional
 * @param parseInteractionsOnly - optional
 * @returns {{contentRevisionId, identifier, responseDeclarations, componentBody, componentBodyAssets, itemBody,
 *              objectType, responseModels, mediaModels, isParsed, language}}
 */
export function parseQTIAssessmentItem(qtiData, language, assessmentRunId, parseInteractionsOnly) {
    const { templateDeclarations, contentRevisionId, itemBody, responseDeclarations, mediaInfo, version, category } = qtiData;

    if (qtiData.identifier === undefined) {
        // create the identifier on the object for cleaner more qti compliant parsing naming conventions
        qtiData.identifier = contentRevisionId;
    }

    const identifier = qtiData.identifier;
    const questionType = qtiData.objectType;
    const itemTypeDetails = qtiData.customFields;
    const itemBodyElement = new DOMParser().parseFromString(itemBody, 'text/xml').firstChild;

    replaceImageUrlsForImgTags(itemBodyElement, assessmentRunId);
    replacePrintedVariables(itemBodyElement, templateDeclarations, qtiData);

    const sanitizedResponseDeclarations = sanitizeResponseDeclarations(responseDeclarations);
    const responseModels = interactionsParser.getInteractions({
        questionIdentifier: identifier,
        responseDeclarations: sanitizedResponseDeclarations,
        itemBodyElement,
        language,
        assessmentRunId,
        templateDeclarations,
        version,
        mediaInfo,
        qtiData,
    });
    const mediaModels = getMediaModels(identifier, itemBodyElement, assessmentRunId, language, mediaInfo);
    const PDFPanelModel = getPDFPanelModel(itemBodyElement, language, assessmentRunId, category);
    const rubricBlockModel = getRubricBlockModel(itemBodyElement);
    const extraParams = { qtiData, responseModels };
    // getItemBodyAsHTML mutates the itemBodyElement object, so it must be called last
    const componentBody = getItemBodyAsHTML(
        identifier,
        itemBodyElement,
        language,
        parseInteractionsOnly,
        questionType,
        itemTypeDetails,
        extraParams,
    );
    const extraAssets = questionAssets.getExtraAssets(itemBody, responseModels);

    const dataToAssign = {
        responseModels,
        mediaModels,
        PDFPanelModel,
        rubricBlockModel,
        componentBody,
        extraAssets,
        isParsed: true,
        language,
        responseDeclarations: sanitizedResponseDeclarations,
    };

    if (qtiData.stimulus) {
        const stimulusData = {
            defaultLanguage: qtiData.defaultLanguage,
            supportedLanguages: qtiData.defaultLanguage,
            ...qtiData.stimulus,
        };
        dataToAssign.stimulus = parseQuestionStimulus(stimulusData, language, assessmentRunId, parseInteractionsOnly);
    }

    return Object.assign({}, qtiData, dataToAssign);
}

/**
 * Entry point for parsing a document. Content gets converted to a DOM element and sent into componentBody parser.
 * The result is merged with the document json on the server, so that the original data is persisted.
 * @param documentData {{contentRevisionId, identifier, objectType, content, mediaInfo}}
 * @param language
 * @param assessmentRunId - optional
 * @returns {{contentRevisionId, identifier, componentBody, content, objectType, mediaModels, isParsed, language}}
 */
export function parseDocumentAssessmentItem(documentData, language, assessmentRunId) {
    if (documentData.identifier === undefined) {
        // create the identifier on the object for cleaner more qti compliant parsing naming conventions
        documentData.identifier = documentData.contentRevisionId;
    }

    let documentRootNode;
    let documentParseError = false;

    const documentParser = new DOMParser({
        /**
         * locator is always need for error position info
         */
        locator: {},
        /**
         * you can override the errorHandler for xml parser
         * @link http://www.saxproject.org/apidoc/org/xml/sax/ErrorHandler.html
         */
        errorHandler: {
            warning: (w) => {
                window.console.warn(w);
            },
            error: (msg) => {
                documentParseError = msg;
            },
            fatalError: (msg) => {
                documentParseError = msg;
            },
        },
    });

    // Document structure is html -> body -> div ....
    documentRootNode = documentParser.parseFromString(documentData.content, 'text/xml').firstChild;

    if (documentParseError) {
        documentRootNode = documentParser.parseFromString('<html><body><div></div></body></html>', 'text/xml');
    }

    replaceImageUrlsForImgTags(documentRootNode, assessmentRunId);

    const contentElement = documentRootNode.getElementsByTagName('div')[0];
    const defaultLanguage = contentElement.getAttribute('data-defaultlanguage');
    const supportedLanguages = contentElement.getAttribute('data-supportedlanguages');
    contentElement.setAttribute(constants.defaultLanguageAttr, defaultLanguage);
    contentElement.setAttribute(constants.supportedLanguagesAttr, supportedLanguages);

    const mediaModels = getMediaModels(documentData.identifier, contentElement, assessmentRunId, language, documentData.mediaInfo);
    const PDFPanelModel = getPDFPanelModel(contentElement, language, assessmentRunId, documentData.category);
    // getComponentBodyAsHTML mutates the contentElement object, so must be called last
    const componentBody = getItemBodyAsHTML(documentData.identifier, contentElement, language);

    return Object.assign({}, documentData, {
        mediaModels,
        PDFPanelModel,
        componentBody,
        isParsed: true,
        documentParseError,
        language,
    });
}

/**
 * Takes in full assessment xml and returns an object that the QTIParser.parse takes as input, by creating a object with an array of
 * responseDeclarations and itemBody as string.
 *
 * @param assessmentXML
 * @returns {{identifier, responseDeclarations, itemBody}}
 */
export function parseAssessmentXML(assessmentXML) {
    const assessmentDoc = new DOMParser().parseFromString(assessmentXML, 'text/xml');
    return parseAssessmentDOM(assessmentDoc.getElementsByTagName('assessmentItem')[0]);
}

export function parseQuestionStimulus(qtiData, language, assessmentRunId, parseInteractionsOnly) {
    if (!qtiData.itemBody) {
        // in question set or test preview mode stimulus is a nested question object property
        // in that case it doesn't have itemBody prop, but has stimulusBody instead
        qtiData.itemBody = qtiData.stimulusBody;
    }
    return parseQTIAssessmentItem(qtiData, language, assessmentRunId, parseInteractionsOnly);
}

/**
 * Entry point for any assessment item. Calls either the QTI parsing or document parsing based on the objectType
 * @param itemData
 * @param language
 * @param assessmentRunId - optional
 * @param parseInteractionsOnly - optional
 * @returns {*}
 */
export function parseAssessmentItem(itemData, language, assessmentRunId, parseInteractionsOnly) {
    switch (itemData.objectType) {
    case objectTypes.DOCUMENT:
        return parseDocumentAssessmentItem({ ...itemData }, language, assessmentRunId);
    case objectTypes.STIMULUS:
        return parseQuestionStimulus({ ...itemData }, language, assessmentRunId, parseInteractionsOnly);
    default:
        return parseQTIAssessmentItem({ ...itemData }, language, assessmentRunId, parseInteractionsOnly);
    }
}
